import i18n from "@verbleif/lib/src/translations/i18n";
import { toast } from "vue-sonner";

export const ACCOUNT_SWITCH_TOAST_KEY = "account-switch-toast";
const TRANSLATION_KEY = "navbar.dropdown";

interface AccountSwitchToast {
  name: string
  locationName: string | null
}

// The switch reloads the tab, so the toast waits in this tab's sessionStorage until the new account is signed in.
export function rememberAccountSwitchToast(toastData: AccountSwitchToast): void {
  try {
    sessionStorage.setItem(ACCOUNT_SWITCH_TOAST_KEY, JSON.stringify(toastData));
  } catch {
    // The toast is a courtesy; a blocked sessionStorage must never stop the switch.
  }
}

function readAccountSwitchToast(): AccountSwitchToast | null {
  try {
    const raw = sessionStorage.getItem(ACCOUNT_SWITCH_TOAST_KEY);
    if (raw === null) {
      return null;
    }
    sessionStorage.removeItem(ACCOUNT_SWITCH_TOAST_KEY);
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !("name" in parsed) || typeof parsed.name !== "string") {
      return null;
    }
    const locationName = "locationName" in parsed && typeof parsed.locationName === "string" && parsed.locationName !== "" ? parsed.locationName : null;
    return { name: parsed.name, locationName };
  } catch {
    return null;
  }
}

const TOASTER_POLL_MS = 200;
const TOASTER_MAX_POLLS = 25;

// vue-sonner drops a toast that is published before its Toaster has mounted, and the first sign-in of a reload can
// finish before the app does.
function whenToasterMounted(show: () => void, polls = 0): void {
  if (document.querySelector("[data-sonner-toaster]") !== null || polls >= TOASTER_MAX_POLLS) {
    show();
    return;
  }
  setTimeout(whenToasterMounted, TOASTER_POLL_MS, show, polls + 1);
}

/** Shows the "Je werkt nu als" toast once after a switch, then forgets it. */
export function showPendingAccountSwitchToast(): void {
  const pending = readAccountSwitchToast();
  if (!pending) {
    return;
  }
  const { t } = i18n.global;
  whenToasterMounted(() => toast.info(t(`${TRANSLATION_KEY}.switched_title`, { name: pending.name }), {
    description: pending.locationName
      ? t(`${TRANSLATION_KEY}.switched_description`, { location: pending.locationName })
      : t(`${TRANSLATION_KEY}.switched_description_no_location`),
  }));
}
